import "./SpaErrorsPage.css"
import React, {useEffect} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch } from "../../redux/store";
import RootState from "../../redux/states/root.state";
import Header from "../../components/common/Header/Header";
import { fetchSpa } from "../../redux/actions/spa.actions";
import NotFound from "../General/NotFound";
import { SpaErrorLabels } from "../../mqttData/errors/spaErrorLabels.enums";
import { SpaStatusLabels } from "../../mqttData/status/spaStatusLabels.enums";
import useMQTT from "../../hooks/useMQTT";
import { formatErrorCode, formatStatusCode } from "../SpaDetails/formatCodes";
import { StonlyLinks } from "./components/stonly.enums";
import { ArcticAbbr } from "./components/arcticAbbr.enums";
import { ArcticLabels } from "./components/arcticLabels.enums";
import usePermissionCheck from "../../hooks/usePermissionCheck";
import { AlertIcon, CheckCircleIcon, ExternalLinkIcon, WarningIcon } from "../../components/common/Icons/Icons";

type Code = keyof typeof ArcticLabels;

interface PanelProps {
  title: string;
  codes: string[];
  isActive: (code: string) => boolean;
  format: (code: string) => string;
  // Errors read red, statuses amber (as the old icons did)
  tone: 'bad' | 'caution';
}

// One list: active items first and highlighted with their "how to fix" link, then the rest muted
const CodePanel: React.FC<PanelProps> = ({title, codes, isActive, format, tone}) => {
  const active = codes.filter(isActive);
  const inactive = codes.filter((code) => !isActive(code));

  const describe = (code: string) => {
    const abbr = ArcticAbbr[code as Code];
    const label = ArcticLabels[code as Code];
    return (
      <>
        <span className="se-code">{format(code)}</span>
        {abbr && <span className="se-abbr">{abbr}</span>}
        <span className="se-label">{label || <span className="se-none">—</span>}</span>
      </>
    );
  };

  return (
    <section className={`se-panel tone-${tone}`} aria-label={title}>
      <div className="se-head">
        <h2 className="se-title">{title}</h2>
        {active.length > 0 ?
          <span className="se-summary active">{active.length} active</span>
          :
          <span className="se-summary clear"><CheckCircleIcon/> All clear</span>}
      </div>

      <div className="se-body">
        {active.length > 0 &&
          <ul className="se-list">
            {active.map((code) => (
              <li key={code} className="se-item active">
                <span className="se-icon" aria-hidden="true">{tone === 'bad' ? <AlertIcon/> : <WarningIcon/>}</span>
                <span className="se-text">{describe(code)}</span>
                {StonlyLinks[code as Code] &&
                  <a className="lp-link se-fix" href={StonlyLinks[code as Code]} target="_blank" rel="noreferrer">
                    How to fix<ExternalLinkIcon/>
                  </a>}
              </li>
            ))}
          </ul>}

        {inactive.length > 0 && <>
          <div className="se-divider"><span>Not active · {inactive.length}</span></div>
          {/* Reference list: code, tag and description in fixed columns so every row lines up */}
          <ul className="se-ref">
            {inactive.map((code) => (
              <li key={code} className="se-ref-row">
                <span className="se-code">{format(code)}</span>
                <span className="se-ref-abbr">{ArcticAbbr[code as Code]}</span>
                <span className="se-ref-label">{ArcticLabels[code as Code] || <span className="se-none">—</span>}</span>
              </li>
            ))}
          </ul>
        </>}
      </div>
    </section>
  );
};

const SpaErrorsPage: React.FC = () => {
  // Data
  const {id} = useParams();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {data:spa, loading:isLoading, error} = useSelector((state: RootState) => state.spa);
  const isConnected = useSelector((state: RootState) =>
    state.spa.currentSpaId === id && state.spa.isCurrentSpaConnected
  );
  const errorsData = useSelector((state: RootState) => state.error.data);
  const statusData = useSelector((state: RootState) => state.status.data);
  const {canAccess: canViewAllErrors} = usePermissionCheck(['view_all_spa_errors']);

  const filteredErrors = canViewAllErrors ? Object.values(SpaErrorLabels).filter(
      (code) => code
    ) : Object.values(SpaErrorLabels).filter(
      (code) => ArcticLabels[code] !== ""
    );

  const filteredStatuses = canViewAllErrors ? Object.values(SpaStatusLabels).filter(
    (code) => code
  ) : Object.values(SpaStatusLabels).filter(
    (code) => ArcticLabels[code] !== ""
  );

  useEffect(() => {
    if (id){
      dispatch(fetchSpa(id));
    }
  }, [dispatch, id]);

  // MQTT Functionality:
  useMQTT(spa.id ?? "");

  if (error){
    return <NotFound/>
  }

  return (
    <div className="page lp-page">
      <Header
        header="Spa Errors"
        headerExtra={spa?.id &&
          <span className="lp-spa-chip">
            <span className={`lp-dot ${isConnected ? 'online' : ''}`} title={isConnected ? 'Online' : 'Offline'}/>
            <span className="visually-hidden">{isConnected ? 'Online' : 'Offline'}</span>
            <a
              href={`/spa/${spa?.id}`}
              className="lp-spa-link"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                e.preventDefault();
                navigate(`/spa/${spa?.id}`);
              }}
            >
              {spa?.nick_name ?? 'n/a'}<ExternalLinkIcon/>
            </a>
            <span className="lp-spa-chip-meta">SN {spa.blue_falls_serial_number ?? spa.arctic_serial_number ?? "n/a"}</span>
          </span>
        }
      />
      <main className="lp-main">
        {isLoading ? (
          <div className="lp-loading"><span className="spinner" role="progressbar" aria-label="Loading spa"/></div>
        ) : (
          <div className="se-grid">
            <CodePanel
              title="Errors"
              codes={filteredErrors}
              isActive={(code) => Boolean(errorsData[SpaErrorLabels[code as keyof typeof SpaErrorLabels]])}
              format={(code) => formatErrorCode(SpaErrorLabels[code as keyof typeof SpaErrorLabels])}
              tone="bad"
            />
            <CodePanel
              title="Statuses"
              codes={filteredStatuses}
              isActive={(code) => Boolean(statusData[SpaStatusLabels[code as keyof typeof SpaStatusLabels]])}
              format={(code) => formatStatusCode(SpaStatusLabels[code as keyof typeof SpaStatusLabels])}
              tone="caution"
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default SpaErrorsPage;
